import { getPagination, getTableFilter, handleUrlChange } from '@TerraCycleUS/loop-antd-filter'
import {
  Button,
  Col,
  notification,
  Popconfirm,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd'
import { compact, isEmpty, orderBy } from 'lodash'
import { PropTypes } from 'prop-types'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  BankDetailsModal,
  Can,
  Copyable,
  Icon,
  IconContainer,
  PageContainer,
  PageTitleRow,
} from '../../components'
import { withConfig, withProfile } from '../../modules'
import {
  createDownloadLink,
  handleBackendError,
} from '../../utils'
import { getCountryList } from '../country-management/api'
import StoreDetailsModal from './store-details-modal/StoreDetailsModal'

import BalanceModal from './BalanceModal'
import BatchPayoutModal from './BatchPayoutModal'
import StoreManagerModal from './store-manager/StoreManagerModal'

import { configPropTypes, userPropTypes } from '../../types'

import { payoutBalance } from '../withdraw-history/api'
import {
  adjustBalanceBetween,
  deleteStore,
  exportStores,
  getStoreList,
  switchVirtualStore,
} from './api'

const statusFilter = [
  { text: 'active', value: 'true' },
  { text: 'inactive', value: 'false' },
]

const virtualFilter = [
  { text: 'virtual', value: 'true' },
  { text: 'standard', value: 'false' },
]

export const Stores = ({ config, user }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isModalVisible, setModalVisibility] = useState({})
  const [isModalLoading, setModalLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState()
  const [stores, setStores] = useState([])
  const [selectedStore, setSelectedStore] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [countryFilters, setCountryFilters] = useState([])
  const [metaData, setMetaData] = useState()

  useEffect(() => {
    if (isEmpty(searchParams.toString()))
      setSearchParams({ items_per_page: 25, page: 1 })
  }, [])

  useEffect(() => {
    getCountryList()
      .then(({ data }) =>
        setCountryFilters(() =>
          orderBy(
            data.data.map(({ code, name, id }) => ({
              code,
              text: name,
              value: id,
            })),
            'text',
            'asc',
          ),
        ),
      )
      .catch((error) => handleFailure(error, 'Fetching the countries failed!'))
  }, [])

  useEffect(() => {
    if (!isEmpty(searchParams.toString()) && !isModalVisible.details)
      fetchStoreList(searchParams.toString())
  }, [searchParams])

  const fetchStoreList = (params) => {
    setIsLoading(true)

    getStoreList(params)
      .then(({ data }) => {
        setMetaData(data.meta)
        setStores(data.data)
        setIsLoading(false)

        if (selectedStore) {
          const updatedStore = data.data.find((store) => store.id === selectedStore.id)
          updatedStore && setSelectedStore(updatedStore)
        }
      })
      .catch((error) => {
        setIsLoading(false)

        handleFailure(error, 'Fetching store list failed!')
      })
  }

  const handleVirtualStore = (selectedStore) => {
    switchVirtualStore(selectedStore.id)
      .then(() => handleSuccess({ description: 'Switched to virtual store!' }))
      .catch((error) =>
        handleFailure(error, 'Change the store to virtual store is failed!'),
      )
  }

  const isSuperAdmin = user?.me.roles.includes('super_admin')

  const handleDeleteStore = (id) => {
    deleteStore(id)
      .then(() => handleSuccess({ description: 'Store deleted successfully!' }, true))
      .catch((error) => handleFailure(error, 'Delete store failed!'))
  }

  const ActionButtons = ({ record, showEditIcon = false }) => (
    <IconContainer>
      {showEditIcon && (
        <Can
          no={() => (
            <Tooltip title="View">
              <Icon
                data-testid="viewBtn"
                name="updateStoreSelenium"
                onClick={() => handleOpenModal('details', record)}
                type="eye"
              />
            </Tooltip>
          )}
          requiredPermission="store.put.store_id"
          yes={() => (
            <Tooltip title="Edit">
              <Icon
                data-testid="editBtn"
                name="updateStoreSelenium"
                onClick={() => handleOpenModal('details', record)}
                type="edit"
              />
            </Tooltip>
          )}
        />
      )}

      <Can
        requiredPermission="payment_method.post"
        yes={() => (
          <Tooltip title="Add bank details">
            <Icon
              data-testid="addBankDetails"
              name="addBankDetailsIconSelenium"
              onClick={() => handleOpenModal('bank', record)}
              type="bank"
            />
          </Tooltip>
        )}
      />

      <Can
        requiredPermission="store.post.store_id_adjust_balance_between"
        yes={() => (
          <Tooltip title="Adjust balance">
            <Icon
              data-testid="addjustBalanceBtn"
              name="adjustBalanceSelenium"
              onClick={() => handleOpenModal('adjust', record)}
              type="adjust"
            />
          </Tooltip>
        )}
      />

      {!record.virtual && (
        <Tooltip title="Connected Users Management">
          <Icon
            data-testid="storeManagerBtn"
            name="storeManagerSelenium"
            onClick={() => handleOpenModal('storeManager', record)}
            type="user"
          />
        </Tooltip>
      )}

      <Can
        requiredPermission="store.put.store_id"
        yes={() => (
          <Tooltip title="Switch to virtual store">
            <Popconfirm
              cancelText="No"
              className="action-list-button"
              okText="Yes"
              onConfirm={() => handleVirtualStore(record)}
              title="Are you sure you want to make this store virtual?"
            >
              <Icon
                data-testid="virtualStoreBtn"
                disabled={record.virtual}
                name="virtualStoreSelenium"
                type="virtual"
              />
            </Popconfirm>
          </Tooltip>
        )}
      />

      {isSuperAdmin && (
        <Tooltip title="Delete store">
          <Popconfirm
            cancelText="No"
            className="action-list-button"
            okText="Yes"
            onConfirm={() => handleDeleteStore(record.id)}
            title="Are you sure you want to delete this store?"
          >
            <Icon
              data-testid="deleteStoreBtn"
              name="deleteStoreSelenium"
              type="delete"
            />
          </Popconfirm>
        </Tooltip>
      )}
    </IconContainer>
  )

  /* eslint-disable */
  const tableColumns = compact([
    {
      className: "storeIdSelenium",
      dataIndex: "id",
      render: (_, record) => (
        <div style={{ maxWidth: 150 }}>
          <Copyable text={record.id} />
        </div>
      ),
      title: "ID",
      width: 60,
      ...getTableFilter({ name: 'store_id' }),
    },
    {
      className: "storeNameSelenium",
      dataIndex: "name",
      title: "Name",
      ...getTableFilter({ name: 'name_cont' }),
    },
    {
      className: "storeCountrySelenium",
      dataIndex: "country_name",
      title: "Country",
      ...getTableFilter({ filterValues: countryFilters, isMultiple: false, name: 'country_id_eq', type: 'checkbox' }),
    },
    {
      children: [
        {
          className: "storeStateSelenium",
          dataIndex: "state",
          title: "State",
        },
        {
          className: "storeCitySelenium",
          dataIndex: "city",
          title: "City",
        },
        {
          className: "storeZipCodeSelenium",
          dataIndex: "zip_code",
          title: "Zip",
        },
        {
          className: "storeAddressLineSelenium",
          dataIndex: "address_line",
          title: "Address Line",
        },
      ],
      className: "storeAddressSelenium",
      dataIndex: "address",
      title: "Address",
      ...getTableFilter({ name: 'state_or_zip_code_or_city_or_address_line_cont' }),
    },
    {
      className: "storeStatusSelenium",
      dataIndex: "active",
      render: (_, record) => (
        <span>{record.active ? "Active" : "Inactive"}</span>
      ),
      title: "Status",
      ...getTableFilter({ filterValues: statusFilter, isMultiple: false, name: 'active_eq', type: 'checkbox' }),
    },
    {
      className: "storeBalanceSelenium",
      dataIndex: "balance",
      render: (_, record) => (
        <div>
          {new Intl.NumberFormat(record.country_locales[0], {
            currency: record.currency,
            style: "currency",
          }).format(record.balance)}
        </div>
      ),
      title: "Balance",
    },
    {
      className: "storeVirtualBoolSelenium",
      dataIndex: "virtual",
      render: (_, record) => (
        <Tag color={record.virtual ? "green" : "blue"}>
          {record.virtual ? "virtual" : "standard"}
        </Tag>
      ),
      title: "Store type",
      ...getTableFilter({ filterValues: virtualFilter, isMultiple: false, name: 'virtual_eq', type: 'checkbox' }),
    },
    {
      className: "storeActionSelenium",
      render: (_, record) => <ActionButtons record={record} showEditIcon />,
      title: "Actions",
    },
  ]);
  /* eslint-enable */

  ActionButtons.propTypes = {
    record: PropTypes.object.isRequired,
    showEditIcon: PropTypes.bool,
  }

  const handleExportStoresCSV = () => {
    exportStores(searchParams.toString())
      .then(({ blob, fileName }) => {
        createDownloadLink(blob, fileName)

        notification.success({
          description: 'Stores exported',
          duration: 3,
          title: 'Success',
        })
      })
      .catch((error) => {
        handleFailure(error, 'Stores export failed!')
      })
  }

  const handleFormSubmit = (actionType, formData) => {
    setModalLoading(true)

    switch (actionType) {
    case 'ADJUST':
      adjustBalanceBetween(formData.store_id, formData)
        .then(() => {
          handleSuccess({ description: 'Balance adjusted' })
          setModalVisibility({
            adjust: false,
            details: isModalVisible.details,
          })
        })
        .catch((error) => handleFailure(error, 'Adjustment failed!'))
      break

    case 'PAYOUT':
      payoutBalance(formData)
        .then(() => {
          handleSuccess({ description: 'Pay out complete' })
          setModalVisibility({
            details: isModalVisible.details,
            payout: false,
          })
        })
        .catch((error) => handleFailure(error, 'Pay out failed!'))
      break

    default:
      setModalLoading(false)
      notification.error({
        description: 'No submission available',
        title: 'Error',
      })
      break
    }
  }

  const handleOpenModal = (modalName, selectedStore) => {
    setSelectedStore(selectedStore)
    setErrorMessage(null)

    setModalVisibility({
      details: isModalVisible.details,
      [modalName]: true,
    })

    setModalLoading(false)
  }

  const handleSuccess = (messageOptions, forceClose) => {
    fetchStoreList(searchParams.toString())

    setErrorMessage(null)
    setModalLoading(false)
    setModalVisibility({
      details: forceClose ? false : isModalVisible.details,
    })

    notification.success({
      title: 'Success',
      ...messageOptions,
    })
  }

  const handleFailure = (error, message) => {
    if (error) {
      console.log(error)
    }

    const errorMessage = handleBackendError(error, message)

    setErrorMessage(errorMessage)
    setModalLoading(false)

    notification.error({
      description: errorMessage,
      message: 'Error',
    })
  }

  return (
    <PageContainer>
      <PageTitleRow title="Stores">
        <Space>
          <Can
            requiredPermission="store.get.export"
            yes={() => (
              <Button
                data-testid="exportBtn"
                name="exportStoreSelenium"
                onClick={handleExportStoresCSV}
              >
                Export CSV
              </Button>
            )}
          />
          <Can
            requiredPermission="store.post.create"
            yes={() => (
              <Button
                data-testid="addNewBtn"
                name="createStoreSelenium"
                onClick={() => handleOpenModal('details')}
                type="primary"
              >
                NEW STORE
              </Button>
            )}
          />
        </Space>
      </PageTitleRow>

      <Row>
        <Col span={24} style={{ overflow: 'scroll' }}>
          <Table
            className="storePageTableSelenium"
            columns={tableColumns}
            dataSource={stores}
            loading={isLoading}
            onChange={(pagination, filters, sorter) => handleUrlChange(pagination, filters, sorter, setSearchParams)}
            pagination={getPagination(metaData?.page, metaData?.items_per_page, metaData?.total_items)}
            rowKey={({ id }) => id}
            scroll={{ x: true }}
          />

          {isModalVisible.details && (
            <StoreDetailsModal
              actionButtons={
                selectedStore ? <ActionButtons record={selectedStore} /> : null
              }
              countries={countryFilters}
              errorMessage={errorMessage}
              handleFailure={handleFailure}
              handleSuccess={handleSuccess}
              hideModal={() => setModalVisibility({})}
              isModalLoading={isModalLoading}
              retailers={config.config.retailers}
              selectedStore={selectedStore}
              setModalLoading={setModalLoading}
              updateSelectedStore={setSelectedStore}
            />
          )}

          {isModalVisible.bank && (
            <BankDetailsModal
              errorMessage={errorMessage}
              handleFailure={handleFailure}
              handleSuccess={handleSuccess}
              hideModal={() =>
                setModalVisibility({ details: isModalVisible.details })
              }
              selectedStore={selectedStore}
            />
          )}

          {isModalVisible.adjust && (
            <BalanceModal
              errorMessage={errorMessage}
              hideModal={() =>
                setModalVisibility({ details: isModalVisible.details })
              }
              isAdjustment={!!isModalVisible.adjust}
              isLoading={isModalLoading}
              onSubmit={handleFormSubmit}
              selectedStore={selectedStore}
            />
          )}

          {isModalVisible.storeManager && (
            <StoreManagerModal
              hideModal={() => setModalVisibility({})}
              selectedStore={selectedStore}
            />
          )}

          {isModalVisible.batchPayout && (
            <BatchPayoutModal
              countries={countryFilters}
              hideModal={() => setModalVisibility({})}
            />
          )}
        </Col>
      </Row>
    </PageContainer>
  )
}

Stores.propTypes = {
  config: configPropTypes,
  user: userPropTypes,
}

export default withConfig(withProfile(Stores))
